import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { tagsService } from '../api/tags.service';
import { TAG_KEYS } from './queryKeys';
import type { TagFormValues } from '../schemas';

/**
 * Mutations for tag CRUD within a project.
 *
 * @remarks
 * All mutations invalidate `TAG_KEYS.byProject` (broad key) instead of the narrow
 * `list` key. This resets every cached page and filter combination for that project,
 * ensuring counts and ordering stay consistent after any write.
 */
export function useCreateTag(projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (payload: TagFormValues) => tagsService.create(projectId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: TAG_KEYS.byProject(orgId ?? '', projectId),
      });
    },
  });
}

export function useUpdateTag(projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: ({ tagId, payload }: { tagId: string; payload: TagFormValues }) =>
      tagsService.update(projectId, tagId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: TAG_KEYS.byProject(orgId ?? '', projectId),
      });
    },
  });
}

export function useDeleteTag(projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (tagId: string) => tagsService.delete(projectId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: TAG_KEYS.byProject(orgId ?? '', projectId),
      });
    },
  });
}
